import { Activity, RotateCcw, Sparkles } from "lucide-react";
import type { CSSProperties } from "react";
import { useEffect, useMemo, useState } from "react";

const MIN_ANIMATION_MS = 4200;
const COLLAPSE_MS = 560;

interface Prize {
  id: string;
  label: string;
  count: number;
  tone: string;
  remaining: number;
  awarded: number;
}

interface DrawResult {
  id: string;
  drawNumber: number;
  prize: {
    id: string;
    label: string;
    tone: string;
  };
  mode: "deck" | "fallback";
  randomSource: "anu-qrng" | "js-fallback";
  qrngSample: number[];
  qrngError?: string;
  remaining: number;
  createdAt: string;
}

interface DrawResponse {
  ok: boolean;
  result: DrawResult;
  state: {
    totalConfigured: number;
    drawCount: number;
    fallbackCount: number;
    remaining: number;
    prizes: Prize[];
  };
}

interface StatsResponse {
  ok: boolean;
  state: DrawResponse["state"];
  history: DrawResult[];
}

type DrawStatus = "idle" | "running" | "settling" | "done" | "error";
type DrawPhase = "ready" | "measure" | "balance" | "digitize" | "extract" | "collapse";

export default function App() {
  const [status, setStatus] = useState<DrawStatus>("idle");
  const [result, setResult] = useState<DrawResult | null>(null);
  const [stats, setStats] = useState<StatsResponse["state"] | null>(null);
  const [history, setHistory] = useState<DrawResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [slowGate, setSlowGate] = useState(false);
  const [phase, setPhase] = useState<DrawPhase>("ready");
  const [visualSample, setVisualSample] = useState<number[] | null>(null);

  useEffect(() => {
    void loadStats();
  }, []);

  const progress = useMemo(() => {
    if (!stats) {
      return 0;
    }

    return Math.min(100, (stats.drawCount / Math.max(stats.totalConfigured, 1)) * 100);
  }, [stats]);

  async function loadStats() {
    const response = await fetch("/api/draw", { headers: { accept: "application/json" } });
    const payload = (await response.json()) as StatsResponse;

    setStats(payload.state);
    setHistory(payload.history ?? []);
  }

  async function runDraw() {
    if (status === "running" || status === "settling") {
      return;
    }

    const startedAt = performance.now();
    setStatus("running");
    setPhase("measure");
    setResult(null);
    setError(null);
    setSlowGate(false);
    setVisualSample(null);

    const phaseTimers = [
      window.setTimeout(() => setPhase("balance"), 900),
      window.setTimeout(() => setPhase("digitize"), 1900),
      window.setTimeout(() => setPhase("extract"), 2900)
    ];
    const slowTimer = window.setTimeout(() => setSlowGate(true), MIN_ANIMATION_MS - 500);

    try {
      const response = await fetch("/api/draw", {
        method: "POST",
        headers: {
          accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Draw failed with HTTP ${response.status}`);
      }

      const payload = (await response.json()) as DrawResponse;
      setVisualSample(payload.result.qrngSample);
      const elapsed = performance.now() - startedAt;
      const waitMs = Math.max(MIN_ANIMATION_MS - elapsed, 0);

      setStatus("settling");
      await new Promise((resolve) => window.setTimeout(resolve, waitMs));
      setSlowGate(false);
      setPhase("collapse");
      await new Promise((resolve) => window.setTimeout(resolve, COLLAPSE_MS));

      setResult(payload.result);
      setStats(payload.state);
      setHistory((current) => [payload.result, ...current].slice(0, 8));
      setStatus("done");
      setPhase("ready");
    } catch (cause) {
      const elapsed = performance.now() - startedAt;
      const waitMs = Math.max(MIN_ANIMATION_MS - elapsed, 0);

      await new Promise((resolve) => window.setTimeout(resolve, waitMs));
      setError(cause instanceof Error ? cause.message : "Draw failed");
      setStatus("error");
      setPhase("ready");
    } finally {
      window.clearTimeout(slowTimer);
      phaseTimers.forEach((timer) => window.clearTimeout(timer));
      setSlowGate(false);
    }
  }

  const isActive = status === "running" || status === "settling";
  const actionLabel = isActive
    ? {
        measure: "측정 중",
        balance: "균형 검출",
        digitize: "디지털화",
        extract: slowGate ? "비트 추출 중" : "비트 추출 중",
        collapse: "결과 고정",
        ready: "추첨 중"
      }[phase]
    : "Draw";

  return (
    <main className="shell">
      <section className="stage">
        <div className="topline">
          <div>
            <p className="eyebrow">ANU QRNG / Cloudflare KV</p>
            <h1>Q Pick</h1>
          </div>
          <div className="counter">
            <Activity size={17} />
            <span>{stats ? `${stats.drawCount.toLocaleString()} draws` : "syncing"}</span>
          </div>
        </div>

        <p className="trust-line">
          호주국립대학교의 실제 QRNG 양자 랜덤 API를 사용하여 공정하게 추첨됩니다.
        </p>

        <QuantumField active={isActive} slow={slowGate} phase={phase} sample={visualSample} />

        <div className="control-row">
          <button className="draw-button" type="button" onClick={runDraw} disabled={isActive}>
            {isActive ? <Sparkles size={21} /> : <RotateCcw size={21} />}
            <span>{actionLabel}</span>
          </button>
          <div className={`meter phase-${phase}`} aria-label="configured prize deck progress">
            <span style={{ width: `${progress}%` }} />
            <i />
          </div>
        </div>

        <ResultPanel result={result} error={error} status={status} phase={phase} />
      </section>

      <aside className="side">
        <div className="inventory">
          <div className="section-title">Deck</div>
          {stats?.prizes.map((prize) => (
            <div className="prize-row" key={prize.id}>
              <span>{prize.label}</span>
              <strong>{prize.remaining.toLocaleString()}</strong>
            </div>
          ))}
        </div>

        <div className="history">
          <div className="section-title">Recent</div>
          {history.length === 0 ? (
            <p className="empty">No records yet</p>
          ) : (
            history.slice(0, 8).map((entry) => (
              <div className="history-row" key={entry.id}>
                <span>#{entry.drawNumber}</span>
                <strong>{entry.prize.label}</strong>
              </div>
            ))
          )}
        </div>
      </aside>
    </main>
  );
}

function QuantumField({
  active,
  slow,
  phase,
  sample
}: {
  active: boolean;
  slow: boolean;
  phase: DrawPhase;
  sample: number[] | null;
}) {
  const visual = createVisualSignal(sample, phase);

  return (
    <div
      className={`homodyne-rig phase-${phase} ${active ? "active" : ""} ${slow ? "slow" : ""}`}
      aria-hidden="true"
    >
      <div className="phase-sweep" />
      <div className="lo-head">
        <span>LO</span>
      </div>
      <div className="vacuum-port">
        <span>VAC</span>
        {Array.from({ length: 24 }, (_, index) => (
          <i key={index} style={{ "--i": index } as CSSProperties} />
        ))}
      </div>
      <div className="lo-beam" />
      <div className="vacuum-beam" />
      <div className="beam-splitter">
        <span>50:50</span>
      </div>
      <div className="out-beam positive" />
      <div className="out-beam negative" />
      <div className="photodiode pd-plus">
        <span>PD+</span>
      </div>
      <div className="photodiode pd-minus">
        <span>PD-</span>
      </div>
      <div className="subtract-bus" />
      <div className="scope">
        <svg viewBox="0 0 108 72" preserveAspectRatio="none" className="scope-wave">
          <polyline points={visual.wavePoints} />
        </svg>
        <div className="sample-gates">
          {visual.sampleBars.map((height, index) => (
            <b key={index} style={{ "--h": `${height}%`, "--i": index } as CSSProperties} />
          ))}
        </div>
        {Array.from({ length: 48 }, (_, index) => (
          <i key={index} style={{ "--i": index } as CSSProperties} />
        ))}
      </div>
      <div className="adc-stack">
        <span>ADC</span>
        {Array.from({ length: 10 }, (_, index) => (
          <i key={index} style={{ "--i": index } as CSSProperties} />
        ))}
      </div>
      <div className="extractor">
        <span>EXT</span>
        {Array.from({ length: 18 }, (_, index) => (
          <b key={index} style={{ "--i": index } as CSSProperties} />
        ))}
        <div className="bitstream">
          {visual.bits.slice(0, 24).map((bit, index) => (
            <em key={index} style={{ "--i": index } as CSSProperties}>
              {bit}
            </em>
          ))}
        </div>
      </div>
      <div className="collapse-plane">
        {Array.from({ length: 28 }, (_, index) => {
          const x = (index * 41) % 100;
          const y = (index * 59) % 100;

          return (
            <i
              key={index}
              style={
                {
                  "--i": index,
                  "--x": x,
                  "--y": y,
                  "--tx": `${(50 - x) * 3.8}px`,
                  "--ty": `${(50 - y) * 1.7}px`
                } as CSSProperties
              }
            />
          );
        })}
      </div>
    </div>
  );
}

function createVisualSignal(sample: number[] | null, phase: DrawPhase) {
  const seed = sample?.length
    ? sample.reduce((accumulator, value, index) => accumulator ^ ((value & 0xffff) << (index * 3)), 0)
    : 0x6d2b79f5;
  const amplitudeByPhase: Record<DrawPhase, number> = {
    ready: 4,
    measure: 13,
    balance: 18,
    digitize: 21,
    extract: 16,
    collapse: 8
  };
  const amplitude = amplitudeByPhase[phase];
  const points = Array.from({ length: 34 }, (_, index) => {
    const noise = seededNoise(seed, index);
    const harmonic = Math.sin((index / 33) * Math.PI * 7 + (seed % 11));
    const y = 36 + noise * amplitude + harmonic * amplitude * 0.32;

    return `${((index / 33) * 108).toFixed(1)},${Math.max(5, Math.min(67, y)).toFixed(1)}`;
  }).join(" ");
  const bits = Array.from({ length: 32 }, (_, index) => {
    const word = sample?.[Math.floor(index / 16)] ?? seed;
    return ((word >> (15 - (index % 16))) & 1).toString();
  });
  const sampleBars = Array.from({ length: 8 }, (_, index) => 18 + Math.abs(seededNoise(seed, index + 40)) * 74);

  return {
    bits,
    sampleBars,
    wavePoints: points
  };
}

function seededNoise(seed: number, index: number) {
  let value = (seed + index * 0x9e3779b9) >>> 0;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;

  return ((value >>> 0) / 0xffffffff) * 2 - 1;
}

function ResultPanel({
  result,
  error,
  status,
  phase
}: {
  result: DrawResult | null;
  error: string | null;
  status: DrawStatus;
  phase: DrawPhase;
}) {
  if (error) {
    return (
      <div className="result-panel error-panel">
        <span>ERROR</span>
        <strong>{error}</strong>
      </div>
    );
  }

  if (!result) {
    const activeCopy: Record<DrawPhase, string> = {
      ready: "진공 입력 대기",
      measure: "진공 요동 측정",
      balance: "차동 신호 검출",
      digitize: "ADC 샘플링",
      extract: "무작위 비트 추출",
      collapse: "결과 수렴"
    };
    const phaseLabel: Record<DrawPhase, string> = {
      ready: "준비",
      measure: "측정",
      balance: "검출",
      digitize: "변환",
      extract: "추출",
      collapse: "수렴"
    };

    return (
      <div className="result-panel placeholder">
        <span>{status === "idle" ? "준비" : phaseLabel[phase]}</span>
        <strong>{activeCopy[phase]}</strong>
      </div>
    );
  }

  return (
    <div className={`result-panel tone-${result.prize.tone}`}>
      <span>#{result.drawNumber} / {result.randomSource}</span>
      <strong>{result.prize.label}</strong>
      <small>
        {result.mode === "fallback" ? "fallback deck" : `${result.remaining.toLocaleString()} left`} · [
        {result.qrngSample.join(", ")}]
      </small>
    </div>
  );
}
